import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { NavigationContext } from "../../../context/navigation-context";
import useForm from "../../../hooks/form-hook";
import useFetch from "../../../hooks/fetchMSSQL-hook";
import NoResults from "../../../components/NoResults/NoResults";
import TerminalForm from "../../../components/Modals/TerminalForm";
import TerminalAdvertisesForm from "../../../components/Modals/TerminalAdvertisesForm";
import tmobileIcon from "../../../assets/images/tmobile_icon.png";
import plusIcon from "../../../assets/images/plus_icon.png";
import { LangContext } from "../../../context/lang-context";
import FormSkel from "../../../components/Modals/FormSkel";
import signalIcon1 from "../../../assets/images/signal-icon1.svg";
import signalIcon2 from "../../../assets/images/signal-icon2.svg";
import signalIcon3 from "../../../assets/images/signal-icon3.svg";
import signalIcon4 from "../../../assets/images/signal-icon4.svg";
import SearchFilter from "../../../components/Filter/SearchFilter";
import { NotificationManager } from "react-notifications";
import { resolveMachine } from "./utils/resolveMachine";

const Terminals = () => {
  const { TRL_Pack } = useContext(LangContext);
  const { fetchMssqlApi } = useFetch();
  const { setHeaderData, state } = useContext(NavigationContext);

  const clientId = (sessionStorage.getItem("clientId") || "").trim();
  const isConsole = clientId === "console";

  const { form, openForm, closeForm } = useForm();

  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [terminals, setTerminals] = useState([]);
  const [filteredTerminals, setFilteredTerminals] = useState([]);
  const [selectedTerminals, setSelectedTerminals] = useState([]);
  const [terminalAdvertisesForm, setTerminalAdvertisesForm] = useState(false);
  const [terminalAdvertises, setTerminalAdvertises] = useState([]);
  const [isSavingTerminalAdvertises, setIsSavingTerminalAdvertises] = useState(false);

  const [terminalToDelete, setTerminalToDelete] = useState(null);
  const [terminalForNewMachine, setTerminalForNewMachine] = useState(null);
  const [terminalsSummary, setTerminalsSummary] = useState(null);

  const [hasPermissionToEditTerminal, setHasPermissionToEditTerminal] = useState(false);

  useEffect(() => {
    if (!isConsole) {
      const ok =
        Array.isArray(state?.permissions) &&
        state.permissions.some((p) => p?.Name === "VD_UPDATE_TERMINALS");
      setHasPermissionToEditTerminal(!!ok);
    } else {
      setHasPermissionToEditTerminal(true);
    }
  }, [state?.permissions, isConsole]);

  const renderTimeDiffBadge = (diff) => {
    if (!diff || diff === "0s") return null;
    const s = String(diff);

    if (s.includes("s") || s.includes("m")) {
      return <div className="badge badge-success align-middle m-1">{s}</div>;
    }
    if (s.includes("h")) {
      return <div className="badge badge-warning align-middle m-1">{s}</div>;
    }
    if (s.includes("d")) {
      return <div className="badge badge-danger align-middle m-1">{s}</div>;
    }
    return <div className="badge badge-secondary align-middle m-1">{s}</div>;
  };

  const fmtMoney = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "0.00";
    return n.toFixed(2);
  };

  const hasTransactionBadge = (value, count) => {
    const numericValue = Number(value);
    const numericCount = Number(count);
    return Number.isFinite(numericValue) && Number.isFinite(numericCount) && numericValue !== 0 && numericCount > 0;
  };

  const getBooleanValue = (value) => {
    return value === true || Number(value) === 1 || String(value).toLowerCase() === "true";
  };

  const canEditConsoleFields = isConsole;

  const getMachines = useCallback(() => {
    if (isConsole) {
      fetchMssqlApi(
        "console-machines-list",
        {},
        (res) => {
          const list = Array.isArray(res) ? res : [];
          const normalized = list.map((m) => ({
            ...m,
            Name: m?.Name || m?.MachineName || m?.MachineFullName || "",
          }));
          setMachines(normalized);
        },
        () => setMachines([])
      );
      return;
    }

    fetchMssqlApi(
      "machines-list",
      {},
      (res) => setMachines(res || []),
      () => setMachines([])
    );
  }, [fetchMssqlApi, isConsole, clientId]);

  const getCustomers = useCallback(() => {
    fetchMssqlApi("customers", {}, (res) => setCustomers(res || []), () => setCustomers([]));
  }, [fetchMssqlApi]);

  const getTerminalInfo = useCallback(() => {
    fetchMssqlApi("/getTerminalsSummary", {}, (res) => {
      setTerminalsSummary(res?.info || null);
    });
  }, [fetchMssqlApi]);

  const reportList = useMemo(() => [
    { reportId: 64, reportColor: "outline-primary" },
    { reportId: 98, reportColor: "primary" }
  ], []);

  const machineTypes = useMemo(() => {
    const types = machines
      .map((machine) => machine?.MachineTypeName || machine?.MachineType)
      .filter(Boolean);

    return [...new Set(types)].sort((a, b) => String(a).localeCompare(String(b)));
  }, [machines]);

  const defaultFilter = useMemo(() => {
    const base = {
      name: "terminals",
      page: 1,
      rowsPerPage: 100,
      sortBy: "none",
      terminalId: null,
      terminalSerialNo: "",
      terminalTypeId: null,
      terminalTypeName: "",
      transactionPeriod: "3d",
      consoleMachineName: "",
      consoleMachineId: null
    };

    if (isConsole) {
      const { machineId, machineName, ...rest } = base;
      return { ...rest, customerId: null, customerName: "" };
    }

    return base;
  }, [isConsole]);

  const [filter, setFilter] = useState(defaultFilter);
  const [loadedTransactionPeriod, setLoadedTransactionPeriod] = useState("3d");
  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 100,
  });

  const rowsPerPage = settingsFilter.rowsPerPage || filter.rowsPerPage || 50;

  const transactionPeriodLabels = {
    "1d": "ostatni dzień",
    "3d": "ostatnie 3 dni",
    "7d": "ostatnie 7 dni",
    month: "od początku miesiąca",
  };

  const transactionPeriodLabel =
    transactionPeriodLabels[loadedTransactionPeriod] || transactionPeriodLabels["3d"];

  const sortDefaults = {
    CustomerName: "ASC",
    LastTrxCreateDateTime: "DESC",
    MachineFullName: "ASC",
    PositiveTrxValue: "DESC",
  };

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || "").split("|");
    return { field, dir: (dir || "").toUpperCase() };
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return "";
    return sortState.dir === "ASC" ? " ▲" : " ▼";
  };

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || "").split("|");
    const curDir = (curDirRaw || "").toUpperCase();

    let nextDir;
    if (curField === field) {
      nextDir = curDir === "ASC" ? "DESC" : "ASC";
    } else {
      nextDir = sortDefaults[field] || "ASC";
    }

    const next = { ...filter, sortBy: `${field}|${nextDir}`, page: 1 };
    setFilter(next);
    getTerminals(next);
  };

  const visibleTerminals = useMemo(() => {
    const from = (Number(filter.page || 1) - 1) * Number(rowsPerPage || 50);
    const to = Number(filter.page || 1) * Number(rowsPerPage || 50);
    return (filteredTerminals || []).slice(from, to);
  }, [filteredTerminals, filter.page, rowsPerPage]);

  const getTerminalKey = (terminal) => String(terminal?.TerminalId || "");

  const advertiseTerminalModels = useMemo(
    () => [
      { name: "N62", key: "1:1", resolution: "480x480", aliases: ["N62", "N6202"] },
      { name: "IM30", key: "9:16", resolution: "720x1280", aliases: ["IM30"] },
      { name: "UN20", key: "9:16", resolution: "720x1280", aliases: ["UN20"] },
    ],
    []
  );

  const getAdvertiseTerminalModel = (terminal) => {
    const model = String(terminal?.Model || "").trim().toUpperCase().replace(/\s/g, "");
    if (!model) return null;

    return advertiseTerminalModels.find((item) =>
      item.aliases.some((alias) => model.includes(alias))
    ) || null;
  };

  const getTerminalScreenData = (terminal) => {
    const advertiseModel = getAdvertiseTerminalModel(terminal);

    if (!advertiseModel) {
      return { key: "", resolution: "", model: "" };
    }

    return {
      key: advertiseModel.key,
      resolution: advertiseModel.resolution,
      model: advertiseModel.name,
    };
  };

  const selectedTerminalRows = useMemo(() => {
    const selected = new Set(selectedTerminals.map(String));
    return (terminals || []).filter((terminal) => selected.has(getTerminalKey(terminal)));
  }, [terminals, selectedTerminals]);

  const selectedScreen = useMemo(() => {
    const screens = selectedTerminalRows
      .map(getTerminalScreenData)
      .filter((screen) => screen.key && screen.model);
    const uniqueKeys = [...new Set(screens.map((screen) => screen.key))];

    if (!screens.length) {
      return { key: "", resolution: "", isMixed: false };
    }

    const resolutionText = advertiseTerminalModels
      .reduce((acc, item) => {
        const hasModel = screens.some((screen) => screen.model === item.name);
        if (!hasModel) return acc;

        const group = acc.find((row) => row.resolution === item.resolution);
        if (group) {
          group.models.push(item.name);
          return acc;
        }

        acc.push({ resolution: item.resolution, models: [item.name] });
        return acc;
      }, [])
      .map((item) => `${item.resolution} (${item.models.join(", ")})`)
      .join(", ");

    return {
      key: uniqueKeys.length === 1 ? uniqueKeys[0] : "MIXED",
      resolution: resolutionText,
      isMixed: uniqueKeys.length > 1,
    };
  }, [selectedTerminalRows, advertiseTerminalModels]);

  const selectedModels = useMemo(() => {
    const modelsCount = selectedTerminalRows
      .map(getAdvertiseTerminalModel)
      .filter(Boolean)
      .reduce((acc, item) => {
        acc[item.name] = (acc[item.name] || 0) + 1;
        return acc;
      }, {});

    return advertiseTerminalModels
      .map((item) => item.name)
      .filter((model) => modelsCount[model])
      .map((model) => `${model} (${modelsCount[model]})`);
  }, [selectedTerminalRows, advertiseTerminalModels]);

  const canSelectTerminal = (terminal) => {
    const id = getTerminalKey(terminal);
    const screenData = getTerminalScreenData(terminal);

    if (!id || !screenData.key) return false;
    if (!selectedTerminals.length || selectedTerminals.includes(id)) return true;

    return screenData.key === selectedScreen.key;
  };

  const visibleTerminalIds = useMemo(() => {
    return visibleTerminals.map(getTerminalKey).filter(Boolean);
  }, [visibleTerminals]);

  const visibleSelectableTerminalIds = useMemo(() => {
    return visibleTerminals
      .filter(canSelectTerminal)
      .map(getTerminalKey)
      .filter(Boolean);
  }, [visibleTerminals, selectedTerminals, selectedScreen.key]);

  const isAllSelected =
    visibleSelectableTerminalIds.length > 0 &&
    visibleSelectableTerminalIds.every((id) => selectedTerminals.includes(id));

  const toggleTerminal = (terminal) => (evt) => {
    evt.stopPropagation();

    const id = getTerminalKey(terminal);
    if (!id) return;

    if (!selectedTerminals.includes(id) && !canSelectTerminal(terminal)) {
      const screenData = getTerminalScreenData(terminal);
      NotificationManager.warning(
        screenData.resolution
          ? `Nie można zaznaczyć terminala o rozdzielczości ${screenData.resolution}`
          : "Ten terminal nie ma obsługiwanego modelu reklamowego",
        screenData.resolution ? "Inna rozdzielczość" : "Nieobsługiwany model",
        3000
      );
      return;
    }

    setSelectedTerminals((prev) =>
      prev.includes(id)
        ? prev.filter((item) => item !== id)
        : prev.concat(id)
    );
  };

  const toggleAllTerminals = () => {
    setSelectedTerminals((prev) => {
      if (isAllSelected) {
        return prev.filter((id) => !visibleSelectableTerminalIds.includes(id));
      }

      return Array.from(new Set(prev.concat(visibleSelectableTerminalIds)));
    });
  };

  const clearSelection = () => setSelectedTerminals([]);

  const clearSelectionWithConfirm = () => {
    if (!window.confirm("Czy na pewno chcesz odznaczyć wszystkie terminale?")) return;
    clearSelection();
  };

  const getAdvertisesForTerminals = () => {
    fetchMssqlApi(
      "advertises",
      {
        method: "POST",
        hideNotification: true,
        data: {
          search: "",
          publishStatus: "",
          packageType: "",
          screenRatioKey: selectedScreen.key,
          isActive: true,
          terminalId: null,
          pageNo: 1,
          pageSize: 1000,
          sortBy: "Name|ASC",
        },
      },
      (response) => setTerminalAdvertises(Array.isArray(response) ? response : []),
      () => {
        setTerminalAdvertises([]);
        NotificationManager.error("Nie udało się pobrać zestawów reklam");
      }
    );
  };

  const openTerminalAdvertisesForm = () => {
    if (!selectedTerminalRows.length) return;

    if (selectedScreen.isMixed) {
      NotificationManager.warning("Wybrane terminale mają różne rozdzielczości ekranów");
      return;
    }

    getAdvertisesForTerminals();
    setTerminalAdvertisesForm(true);
  };

  const closeTerminalAdvertisesForm = () => setTerminalAdvertisesForm(false);

  const toggleTerminalFromAdvertisesForm = (terminal) => {
    const id = getTerminalKey(terminal);
    if (!id) return;

    setSelectedTerminals((prev) => prev.filter((item) => item !== id));
  };

  const submitTerminalAdvertises = ({ advertiseId, mode }) => {
    if (!selectedTerminalRows.length) {
      NotificationManager.warning("Wybierz co najmniej jeden terminal");
      return;
    }

    setIsSavingTerminalAdvertises(true);

    fetchMssqlApi(
      "terminal/advertise",
      {
        method: "POST",
        data: {
          advertiseId,
          mode,
          terminalIds: selectedTerminalRows.map((terminal) => Number(terminal.TerminalId)).filter(Number.isFinite),
          terminalSerialNos: selectedTerminalRows.map((terminal) => ({
            TerminalId: Number(terminal.TerminalId) || null,
            SerialNo: String(terminal.SerialNo || terminal.TerminalSerialNo || "").trim(),
          })),
        },
        showLoader: true,
      },
      (response) => {
        NotificationManager.success(response?.message || "Zmieniono reklamy na terminalach");
        setIsSavingTerminalAdvertises(false);
        setTerminalAdvertisesForm(false);
        clearSelection();
        getTerminals(filter, rowsPerPage, filter.page);
      },
      (err) => {
        setIsSavingTerminalAdvertises(false);
        NotificationManager.error(err?.message || "Nie udało się zmienić reklam na terminalach");
      }
    );
  };

  const getTerminals = (f, pageSize, page) => {
    const requestedTransactionPeriod = f?.transactionPeriod || "3d";

    fetchMssqlApi(
      "terminals",
      {
        method: "POST",
        hideNotification: true,
        data: {
          customerAbbr: clientId,
          customerId: f?.customerId,
          terminalId: f?.terminalId,
          terminalTypeId: f?.terminalTypeId,
          terminalSerialNo: f?.terminalSerialNo,
          machineId: f?.consoleMachineId,
          transactionPeriod: requestedTransactionPeriod,
          sortBy: f?.sortBy,
        }, showLoader: true},
      (response) => {
        const list = response || [];
        setTerminals(list);
        setFilteredTerminals(list);
        setLoadedTransactionPeriod(requestedTransactionPeriod);
        setSelectedTerminals([]);
      },
    );
  };

  const findCustomerByTerminal = (terminalId) => {
    if (!terminalId) return null;

    const name = terminals.find((t) => t?.TerminalId === terminalId)?.CustomerName || null;
    const abbr = customers.find((c) => c?.Name === name)?.Abbreviation || null;
    return abbr;
  };

  const findCustomerDataByTerminal = (terminal) => {
    if (!terminal) return null;

    return (
      customers.find(
        (customer) => Number(customer?.CustomerId) === Number(terminal?.CustomerId)
      ) ||
      customers.find(
        (customer) =>
          customer?.Name === terminal?.CustomerName ||
          customer?.Abbreviation === terminal?.Abbreviation
      ) ||
      null
    );
  };

  const getCustomerLabel = (terminal) => {
    const customer = findCustomerDataByTerminal(terminal);

    if (customer) {
      return `${customer.Name} (${customer.Abbreviation})`;
    }

    return terminal?.CustomerName || terminal?.Abbreviation || "";
  };

  const handleAddMachine = (terminal) => {
    if (!terminal?.TerminalId) {
      NotificationManager.error("Nie udało się odczytać danych terminala");
      return;
    }

    setTerminalForNewMachine(terminal);
    closeForm();
  };

  const closeMachineForm = () => {
    setTerminalForNewMachine(null);
  };

  const submitMachine = (evt) => {
    evt.preventDefault();

    const machineName = String(evt.target.elements.machineName?.value || "").trim();
    const serialNo = String(evt.target.elements.machineSerialNo?.value || "").trim();
    const machineType = String(evt.target.elements.machineType?.value || "").trim();

    if (!machineName || !serialNo || !machineType) {
      NotificationManager.warning("Uzupełnij wszystkie pola");
      return;
    }

    const customer = findCustomerDataByTerminal(terminalForNewMachine);
    const customerId =
      Number(terminalForNewMachine?.CustomerId || customer?.CustomerId) || null;
    const terminalId = Number(terminalForNewMachine?.TerminalId) || null;

    if (!customerId || !terminalId) {
      NotificationManager.error("Nie udało się ustalić klienta lub terminala");
      return;
    }

    const useVMCTrx = getBooleanValue(terminalForNewMachine?.UseVMCTrx);
    const useTrxForSales = getBooleanValue(
      terminalForNewMachine?.UseTrxForSales
    );
    const useGenerateMachine = getBooleanValue(
      terminalForNewMachine?.UseGenerateMachine
    );

    fetchMssqlApi(
      "machine",
      {
        method: "POST",
        data: {
          MachineName: machineName,
          SerialNo: serialNo,
          MachineType: machineType,
          ClientId: customerId,
          TerminalId: terminalId,
          UseVMCTrx: useVMCTrx,
          UseTrxForSales: useTrxForSales,
        },
        showLoader: true,
      },
      () => {
        fetchMssqlApi(
          "/terminal/machine",
          {
            method: "POST",
            data: {
              TerminalId: terminalId,
              UseTrxForSales: useTrxForSales,
              UseVMCTrx: useVMCTrx,
              MachineFullName: machineName,
              MachineSerialNo: serialNo,
              CustomerId: isConsole ? customerId : null,
              UseGenerateMachine: useGenerateMachine,
            },
          },
          () => {
            setTerminalForNewMachine(null);
            getMachines();
            getTerminals(filter, rowsPerPage, filter.page);
          },
          (err) => {
            NotificationManager.error(
              err?.message || "Automat został dodany, ale nie udało się przypisać terminala"
            );
          }
        );
      },
      (err) => {
        NotificationManager.error(err?.message || "Nie udało się dodać automatu");
      }
    );
  };

  const submitTerminal = (evt) => {
    evt.preventDefault();
    const { useTrxForSales, machine, useVMCTrx, useGenerateMachine, isCustomerSIM, isForTests, isForRent, rentalPrice, simDateTime } = evt.target.elements;
    const { serialNo, terminalType, clientName } = evt.target.elements;

    const toDelete =
      form !== "new" &&
      form !== "acceptForm" &&
      evt.target.elements.isActive.value === "false";

    if (toDelete) {
      setTerminalToDelete(form);
      openForm("acceptForm")();
      return;
    }

    if (form === "acceptForm") {
      fetchMssqlApi(
        `terminal/${terminalToDelete}`,
        { method: "DELETE", data: {} },
        () => {
          setTerminalToDelete(null);
          getTerminals();
          closeForm();
        }
      );
      return;
    }

    if (form === "new") {
      const SerialNo = serialNo.value;
      const TerminalType = terminalType.value;
      const ClientName = clientName.value;

      const terminalTypeMap = new Map([
        ["Vendotek", 1],
        ["PAX", 2],
        ["NAYAX", 3],
        ["Newland", 4],
        ["NEXGO", 5],
      ]);

      if (!SerialNo || !ClientName || !TerminalType) {
        alert("SerialNo or ClientName or TerminalType is empty");
        return;
      }

      const existing = terminals.find((t) => t?.SerialNo === SerialNo);
      if (existing && existing?.TerminalTypeName === TerminalType) {
        alert("terminal already created");
        return;
      }

      const customerId = customers.find((c) => `${c.Name} (${c.Abbreviation})` === ClientName)?.CustomerId || null;

      const terminalTypeId = terminalTypeMap.get(TerminalType);

      if (!terminalTypeId || typeof customerId !== "number") {
        alert("you input incorrect data");
        return;
      }

      const data = {
        TerminalTypeId: terminalTypeId,
        ClientId: customerId,
        SerialNo,
        IsCustomerSIM: canEditConsoleFields ? Boolean(isCustomerSIM?.checked) : false,
        SimDateTime: canEditConsoleFields ? (simDateTime?.value || null) : null,
        IsForTests: canEditConsoleFields ? Boolean(isForTests?.checked) : false,
        IsForRent: canEditConsoleFields ? Boolean(isForRent?.checked) : false,
        RentalPrice:
          canEditConsoleFields && isForRent?.checked && rentalPrice?.value !== ""
            ? Number(rentalPrice.value)
            : null,
      };

      fetchMssqlApi("terminal", { method: "POST", data }, () => {
        getTerminals();
        closeForm();
      });

      return;
    }

    const saveConsoleFields = (terminalId, onSuccess) => {
      if (!canEditConsoleFields) {
        onSuccess();
        return;
      }

      const currentTerminal = (filteredTerminals || []).find(
        (terminal) => Number(terminal?.TerminalId) === Number(terminalId)
      );

      const selectedCustomerId = isConsole
        ? customers.find((c) => `${c.Name} (${c.Abbreviation})` === clientName.value)?.CustomerId
        : currentTerminal?.CustomerId;

      fetchMssqlApi(
        `terminal/${terminalId}`,
        {
          method: "PUT",
          data: {
            SerialNo: currentTerminal?.SerialNo,
            ClientId: selectedCustomerId ?? currentTerminal?.CustomerId,
            IsCustomerSIM: Boolean(isCustomerSIM?.checked),
            SimDateTime: simDateTime?.value || null,
            IsForTests: Boolean(isForTests?.checked),
            IsForRent: Boolean(isForRent?.checked),
            RentalPrice:
              isForRent?.checked && rentalPrice?.value !== ""
                ? Number(rentalPrice.value)
                : null,
          },
        },
        onSuccess
      );
    };

    if (form !== "new" && !String(machine.value || "").length) {
      const TerminalId = form;

      saveConsoleFields(TerminalId, () => {
        fetchMssqlApi(
          "/terminal/machine",
          {
            method: "POST",
            data: {
              TerminalId,
              UseTrxForSales: useTrxForSales.checked,
              UseVMCTrx: useVMCTrx.checked,
              MachineFullName: null,
              MachineSerialNo: null,
              CustomerId: isConsole ? customers.find((c) => `${c.Name} (${c.Abbreviation})` === clientName.value)?.CustomerId : null,
              UseGenerateMachine: useGenerateMachine.checked,
            },
          },
          () => {
            closeForm();
            getTerminals(filter, rowsPerPage, filter.page);
          }
        );
      });

      return;
    }

    if (form !== "new" && String(machine.value || "").length) {
      const TerminalId = form;

      const selectedCustomer = isConsole
        ? customers.find((c) => `${c.Name} (${c.Abbreviation})` === clientName.value)
        : null;
      const { machineName, machineSerialNo } = resolveMachine(
        machines,
        machine.value,
        selectedCustomer?.Abbreviation
      );

      if (!machineName || !machineSerialNo) {
        NotificationManager.error("Nie znaleziono automatu lub jego numeru seryjnego. Wybierz automat z listy i spróbuj ponownie.");
        return;
      }

      saveConsoleFields(TerminalId, () => {
        fetchMssqlApi(
          "/terminal/machine",
          {
            method: "POST",
            data: {
              TerminalId,
              UseTrxForSales: useTrxForSales.checked,
              UseVMCTrx: useVMCTrx.checked,
              MachineFullName: machineName,
              MachineSerialNo: machineSerialNo,
              CustomerId: isConsole
                ? customers.find((c) => `${c.Name} (${c.Abbreviation})` === clientName.value)?.CustomerId
                : null,
              UseGenerateMachine: useGenerateMachine.checked,
            },
          },
          () => {
            closeForm();
            getTerminals(filter, rowsPerPage, filter.page);
          }
        );
      });
    }
  };

  useEffect(() => {
    setSelectedTerminals((prev) => {
      const ids = (filteredTerminals || []).map(getTerminalKey).filter(Boolean);
      const next = prev.filter((id) => ids.includes(id));

      return next.length === prev.length ? prev : next;
    });
  }, [filteredTerminals]);

  useEffect(() => {
    setHeaderData({ text: TRL_Pack.navigation.terminals });
    getMachines();
    getCustomers();
    if (!isConsole) getTerminalInfo();
  }, []);

  return (
    terminals && (
      <div className="filter-icon-fix">
        <SearchFilter
          filterName="terminals"
          defaultFilter={defaultFilter}
          reportList={reportList}
          onFilterChange={(f) => setFilter(f)}
          onSettingsChange={(s) => setSettingsFilter(s)}
          search={getTerminals}
          totalCount={filteredTerminals.slice((filter.page - 1) * rowsPerPage, filter.page * rowsPerPage).length}
          dataList={filteredTerminals}
          forNow
        />

        {isConsole && (
          <button
            className="d-block btn btn-link text-decoration-none ml-auto my-2 mr-1"
            onClick={openForm()}
          >
            <i className="fas fa-plus mr-2" /> Dodaj terminal
          </button>
        )}

        {terminalsSummary && (
          <div className="w-100 card-header d-flex justify-content-between align-items-center">
            {terminalsSummary.map((i, idx) => (
              <div key={i.Id || idx}>
                {i.Name}: {i.Count}
              </div>
            ))}
          </div>
        )}

        {selectedTerminals.length > 0 && (
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
            <div className="d-flex align-items-center flex-wrap">
              <button
                type="button"
                className="btn btn-outline-secondary mt-1 mb-1 mr-2"
                onClick={openTerminalAdvertisesForm}
              >
                Edytuj reklamy
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary mt-1 mb-1 mr-2"
                onClick={clearSelectionWithConfirm}
              >
                <i className="fas fa-times" />
              </button>

              <div className="mb-1 font-weight-bold pe-none info-section-alignment" style={{ fontSize: "9px" }}>
                <div>Wybrane terminale: {selectedTerminals.length}</div>
                <div>Modele: {selectedModels.length ? selectedModels.join(", ") : "-"}</div>
                <div>Rozdzielczość: {selectedScreen.resolution || "-"}</div>
              </div>
            </div>
          </div>
        )}

        {terminals.length ? (
          <>
            {!filteredTerminals.length ? (
              <NoResults />
            ) : (
              <div
                className="overflow-auto custom-scroll"
                style={{ maxHeight: `${window.innerHeight - 196}px`, overflowY: "auto" }}
              >
                <table className="table table-striped border">
                  <thead>
                    <tr>
                      <th className="p-1 align-middle text-center" style={{ width: 45 }}>
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          disabled={!visibleSelectableTerminalIds.length}
                          onChange={toggleAllTerminals}
                        />
                      </th>

                      <th className="p-1 align-middle">
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <div className="text-nowrap">{TRL_Pack.terminals.client}{arrow("CustomerName")}</div>
                          <div className="text-nowrap">
                            Typ terminala / {TRL_Pack.terminals.serialNo}
                          </div>
                          <div className="text-nowrap">Wersja systemu / Wersja aplikacji</div>
                        </div>
                      </th>

                      <th
                        className="p-1 align-middle"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("LastTrxCreateDateTime")}
                      >
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <div className="text-nowrap">
                            Ostatnio online / Ostatnia transakcja{arrow("LastTrxCreateDateTime")}
                          </div>
                          <div className="text-nowrap">Status ostatniej transakcji / terminala</div>
                        </div>
                      </th>

                      <th
                        className="p-1 align-middle"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("MachineFullName")}
                      >
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <div className="text-nowrap">Automat{arrow("MachineFullName")}</div>
                          <div className="text-nowrap">
                            Sposób rozliczania sprzedaży / gospodarka magazynowa
                          </div>
                        </div>
                      </th>

                      <th className="p-1 align-middle">
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <div className="text-nowrap">Jakość (GSM) / Typ połączenia / IP</div>
                          <div className="text-nowrap">Operator GSM / WiFi / SIM Lock / Nr SIM</div>
                        </div>
                      </th>

                      <th
                        className="p-1 align-middle"
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("PositiveTrxValue")}
                      >
                        <div className="d-flex flex-column justify-content-center align-items-center">
                          <div className="text-nowrap">Transakcje ({transactionPeriodLabel})</div>
                          <div className="text-nowrap">Razem{arrow("PositiveTrxValue")}</div>
                          <div className="text-nowrap">Bezgotówkowe / Gotówkowe</div>
                          <div className="text-nowrap">Zwroty / Odmowy bezgotówkowe</div>
                        </div>
                      </th>

                      <th className="p-1 align-middle"></th>
                    </tr>
                  </thead>

                  <tbody>
                    {visibleTerminals.map((terminal, idx) => {
                      const terminalKey = getTerminalKey(terminal);

                      return (
                        <tr key={terminal?.TerminalId || idx}>
                          <td className="p-1 text-center align-middle">
                            <input
                              type="checkbox"
                              checked={selectedTerminals.includes(terminalKey)}
                              disabled={!canSelectTerminal(terminal)}
                              title={
                                !canSelectTerminal(terminal)
                                  ? getTerminalScreenData(terminal).resolution
                                    ? `Inna rozdzielczość: ${getTerminalScreenData(terminal).resolution}`
                                    : "Nieobsługiwany model reklamowy"
                                  : ""
                              }
                              onChange={toggleTerminal(terminal)}
                            />
                          </td>

                          <td className="p-1">
                          <div className="d-flex flex-column justify-content-center align-items-center">
                            <div className="text-nowrap">{terminal?.CustomerName}</div>
                            <div className="text-nowrap">
                              {terminal?.TerminalTypeName} {terminal?.Model || ""} {terminal?.SerialNo}
                            </div>
                            {(terminal?.OSVersion || terminal?.AppVersion) && (
                              <div className="text-nowrap">
                                {[terminal?.OSVersion, terminal?.AppVersion].filter(Boolean).join(" ")}
                              </div>
                            )}
                          </div>
                        </td>

                        <td className="p-1">
                          <div className="d-flex flex-column justify-content-center align-items-center">
                            <div className="d-flex flex-row justify-content-center align-items-center">
                              {renderTimeDiffBadge(terminal?.LastSeenTimeDiff)}
                              {renderTimeDiffBadge(terminal?.LastTrxTimeDiff)}
                            </div>

                            <div className="d-flex flex-column justify-content-center align-items-center">
                              <div className="d-flex flex-row justify-content-center align-items-center align-middle">
                                {terminal?.LastTrxStatus &&
                                  Number((terminal?.LastTrxPrice).toString().replace('.', ',')) > 0 &&
                                  String(terminal?.LastTrxStatus).includes("A") && (
                                    <h6>
                                      <div className="fas fa-s fa-coins" />
                                    </h6>
                                  )}

                                {terminal?.LastTrxStatus &&
                                  Number((terminal?.LastTrxPrice).toString().replace('.', ',')) > 0 &&
                                  !String(terminal?.LastTrxStatus).includes("A") &&
                                  terminal?.TerminalTypeName !== "Newland" && (
                                    <h6>
                                      <div className="fas fa-s fa-credit-card" />
                                    </h6>
                                  )}

                                {(String(terminal?.LastTrxStatus || "").includes("V_S") ||
                                  String(terminal?.LastTrxStatus || "").includes("A")) && (
                                  <h6 className="d-flex align-items-center justify-content-center">
                                    <div className="badge badge-success align-middle m-1">
                                      {fmtMoney(terminal?.LastTrxPrice).replace('.', ',')}zł ({terminal?.LastTrxProductNo})
                                    </div>
                                  </h6>
                                )}

                                {String(terminal?.LastTrxStatus || "").includes("R_S") && (
                                  <h6>
                                    <div className="badge badge-danger align-middle m-1">
                                      {fmtMoney(terminal?.LastTrxPrice).replace('.', ',')}zł ({terminal?.LastTrxProductNo})
                                    </div>
                                  </h6>
                                )}

                                {String(terminal?.LastTrxStatus || "").includes("T_F") && (
                                  <h6>
                                    <div className="badge badge-warning align-middle m-1">
                                      {fmtMoney(terminal?.LastTrxPrice).replace('.', ',')}zł ({terminal?.LastTrxProductNo})
                                    </div>
                                  </h6>
                                )}

                                {terminal?.AppState && String(terminal.AppState).includes("detect") && (
                                  <h6>
                                    <div className="badge badge-success align-middle m-1">VMC</div>
                                  </h6>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="p-1">
                          <div className="d-flex flex-column justify-content-center align-items-center">
                            <div className="text-wrap">{terminal?.MachineFullName}</div>

                            <div className="text-nowrap">
                              {getBooleanValue(terminal?.UseTrxForSales) && getBooleanValue(terminal?.UseVMCTrx) && (
                                <h6>
                                  <div className="badge badge-primary align-middle m-2">Transakcje</div>
                                </h6>
                              )}
                              {!getBooleanValue(terminal?.UseTrxForSales) && (
                                <h6>
                                  <div className="badge badge-info align-middle m-2">EVADTS / manualny</div>
                                </h6>
                              )}
                            </div>

                            <div className="text-nowrap">
                              {getBooleanValue(terminal?.UseVMCTrx) && (
                                <h6>
                                  <div className="badge badge-dark align-middle m-1">
                                    Rozliczanie sprzedaży przez Transakcje R24 VMC
                                  </div>
                                </h6>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="p-1">
                          <div className="d-flex flex-column justify-content-center align-items-center">
                            <div className="d-flex flex-row justify-content-center align-items-center flex-wrap">
                              {Number(terminal?.GSMLevel) > 0 && (
                                <h6>
                                  <div className="badge badge-success align-middle m-1 d-flex justify-content-center align-items-center">
                                    <img
                                      src={
                                        Number(terminal?.GSMLevel) === 1
                                          ? signalIcon1
                                          : Number(terminal?.GSMLevel) === 2
                                          ? signalIcon2
                                          : Number(terminal?.GSMLevel) === 3
                                          ? signalIcon3
                                          : signalIcon4
                                      }
                                      alt="Signal icon"
                                      style={{ width: "13px", padding: "2px", filter: "invert(1)" }}
                                    />
                                  </div>
                                </h6>
                              )}

                              {terminal?.CurrentNetworkType && (
                                <h6>
                                  <div className="badge badge-info align-middle m-1">{terminal.CurrentNetworkType}</div>
                                </h6>
                              )}

                              {terminal?.CurrentIP && (
                                <h6>
                                  <div className="badge badge-secondary align-middle m-1">{terminal.CurrentIP}</div>
                                </h6>
                              )}
                            </div>

                            <div className="d-flex flex-row align-middle mt-1">
                              {terminal?.SIMOperator === "Plus" && (
                                <img src={plusIcon} height="25" className="mr-1" alt="Plus" />
                              )}

                              {(terminal?.SIMOperator === "T-Mobile" ||
                                terminal?.SIMOperator === "T-Mobile #5GBardziej") && (
                                <img src={tmobileIcon} height="25" className="mr-1" alt="T-Mobile" />
                              )}

                              {terminal?.SIMOperator &&
                                terminal.SIMOperator !== "Plus" &&
                                terminal.SIMOperator !== "T-Mobile" &&
                                terminal.SIMOperator !== "T-Mobile #5GBardziej" && (
                                  <div className="badge badge-success align-middle m-1">
                                    {terminal.SIMOperator}
                                  </div>
                                )}

                              {terminal?.IsSIMNetworkLocked === "true" && (
                                <div className="badge badge-warning align-middle m-1">SIM Lock</div>
                              )}

                              {terminal?.IsWiFiEnabled === "true" && (
                                <h6>
                                  <div className="badge badge-success align-middle m-1">WiFi</div>
                                </h6>
                              )}

                              {terminal?.SIMSerial && (
                                <h6>
                                  <div className="badge badge-info align-middle m-1">{terminal.SIMSerial}</div>
                                </h6>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="p-1">
                          <div className="d-flex flex-column justify-content-center align-items-center">
                            <div className="d-flex flex-wrap justify-content-center align-items-center">
                              {hasTransactionBadge(terminal?.PositiveTrxValue, terminal?.PositiveTrxCount) && (
                                <h5 className="m-1">
                                  <div className="badge badge-primary align-middle text-nowrap">
                                    {fmtMoney(terminal?.PositiveTrxValue).replace('.', ',')}zł{" "}
                                    <span className="badge badge-light align-middle">
                                      {terminal?.PositiveTrxCount}
                                    </span>
                                  </div>
                                </h5>
                              )}
                            </div>

                            <div className="d-flex flex-wrap justify-content-center align-items-center">
                              {hasTransactionBadge(terminal?.PositiveCashlessValue, terminal?.PositiveCashlessCount) && (
                                <h6 className="d-inline-flex align-items-center flex-nowrap mx-1 my-1">
                                  <div className="fas fa-xs fa-credit-card mr-1" />{" "}
                                  <div className="badge badge-success align-middle text-nowrap">
                                    {fmtMoney(terminal?.PositiveCashlessValue).replace('.', ',')}zł{" "}
                                    <span className="badge badge-light align-middle">
                                      {terminal?.PositiveCashlessCount}
                                    </span>
                                  </div>
                                </h6>
                              )}

                              {hasTransactionBadge(terminal?.PositiveCashValue, terminal?.PositiveCashCount) && (
                                <h6 className="d-inline-flex align-items-center flex-nowrap mx-1 my-1">
                                  <div className="fas fa-xs fa-coins mr-1" />{" "}
                                  <div className="badge badge-success align-middle text-nowrap">
                                    {fmtMoney(terminal?.PositiveCashValue).replace('.', ',')}zł{" "}
                                    <span className="badge badge-light align-middle">
                                      {terminal?.PositiveCashCount}
                                    </span>
                                  </div>
                                </h6>
                              )}
                            </div>

                            <div className="d-flex flex-wrap justify-content-center align-items-center">
                              {hasTransactionBadge(terminal?.RefundCashlessValue, terminal?.RefundCashlessCount) && (
                                <h6 className="d-inline-flex align-items-center flex-nowrap mx-1 my-1">
                                  <div className="fas fa-xs fa-exclamation-triangle mr-1" />{" "}
                                  <div className="badge badge-danger align-middle text-nowrap">
                                    {fmtMoney(terminal?.RefundCashlessValue).replace('.', ',')}zł{" "}
                                    <span className="badge badge-light align-middle">
                                      {terminal?.RefundCashlessCount}
                                    </span>
                                  </div>
                                </h6>
                              )}

                              {hasTransactionBadge(terminal?.FailedCashlessValue, terminal?.FailedCashlessCount) && (
                                <h6 className="d-inline-flex align-items-center flex-nowrap mx-1 my-1">
                                  <div className="fas fa-xs fa-stop-circle mr-1" />{" "}
                                  <div className="badge badge-warning align-middle text-nowrap">
                                    {fmtMoney(terminal?.FailedCashlessValue).replace('.', ',')}zł{" "}
                                    <span className="badge badge-light align-middle">
                                      {terminal?.FailedCashlessCount}
                                    </span>
                                  </div>
                                </h6>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="p-1">
                          {hasPermissionToEditTerminal && (
                            <button
                              className="btn btn-link link-icon"
                              onClick={openForm(terminal?.TerminalId)}
                            >
                              <i className="fas fa-edit" />
                            </button>
                          )}
                        </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <NoResults />
        )}

        {terminalAdvertisesForm && (
          <TerminalAdvertisesForm
            selectedTerminals={selectedTerminalRows}
            advertises={terminalAdvertises}
            screenData={selectedScreen}
            handleSubmit={submitTerminalAdvertises}
            handleClose={closeTerminalAdvertisesForm}
            handleTerminalToggle={toggleTerminalFromAdvertisesForm}
            isSaving={isSavingTerminalAdvertises}
          />
        )}

        {form && form !== "acceptForm" && (
          <TerminalForm
            terminalData={form !== "new" ? filteredTerminals.find((t) => t?.TerminalId === form) : null}
            machines={machines}
            handleSubmit={submitTerminal}
            handleClose={closeForm}
            handleAddMachine={handleAddMachine}
            abbr={findCustomerByTerminal(form)}
            customers={customers}
            canEditConsoleFields={canEditConsoleFields}
          />
        )}

        {terminalForNewMachine && (
          <FormSkel headerText="Nowy automat" handleClose={closeMachineForm}>
            <form onSubmit={submitMachine} id="modal-form" autoComplete="off">
              <div className="form-group">
                <label className="h6 primary align-middle m-1">Klient</label>
                <input
                  type="text"
                  className="mt-2 form-control form-control-sm"
                  value={getCustomerLabel(terminalForNewMachine)}
                  disabled
                />

                <label className="h6 primary align-middle m-1 mt-3">
                  Nazwa automatu
                </label>
                <input
                  type="text"
                  name="machineName"
                  className="mt-2 form-control form-control-sm"
                  autoComplete="off"
                  required
                />

                <label className="h6 primary align-middle m-1 mt-3">
                  Numer seryjny
                </label>
                <input
                  type="text"
                  name="machineSerialNo"
                  className="mt-2 form-control form-control-sm"
                  autoComplete="off"
                  required
                />

                <label className="h6 primary align-middle m-1 mt-3">
                  Typ automatu
                </label>
                <input
                  type="text"
                  name="machineType"
                  className="mt-2 form-control form-control-sm"
                  list="machineTypesLookup"
                  autoComplete="off"
                  required
                />

                <datalist id="machineTypesLookup">
                  {machineTypes.map((machineType) => (
                    <option value={machineType} key={machineType} />
                  ))}
                </datalist>
              </div>
            </form>
          </FormSkel>
        )}

        {form && form === "acceptForm" && (
          <FormSkel handleClose={closeForm} acceptForm={"true"}>
            <form onSubmit={submitTerminal} id="modal-form">
              {`Czy na pewno chcesz usunąć terminal? Jego przywrócenie będzie wymagało kontaktu ze Wsparciem Technicznym.`}
            </form>
          </FormSkel>
        )}
      </div>
    )
  );
};

export default Terminals;
